import { useRef } from "react";
import { MODULE_LABELS as CANONICAL_MODULE_LABELS } from "@/lib/modules";
import { toggleVariants } from "@/components/ui/toggle";
import { cn } from "@/lib/utils";

const MODULE_LABELS = CANONICAL_MODULE_LABELS as Record<string, string>;

// Accessible module filter chip group, shared across the student-facing
// surfaces (the assessments catalogue grid on the Dashboard and the
// recent-attempts table on My-progress). Implemented as a radiogroup with
// arrow-key roving focus and exactly one tabbable radio so keyboard users
// land on the current selection. "all" leads so the default (no module
// filter) is one keystroke away. Previously lived inline in Dashboard.tsx;
// lifted here verbatim so a second surface can reuse it without a copy.
export function ModuleFilterGroup({
  modules,
  value,
  onChange,
  label = "Filter assessments by module",
}: {
  modules: string[];
  value: string;
  onChange: (module: string) => void;
  label?: string;
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  function focusAndSelect(index: number) {
    const next = (index + modules.length) % modules.length;
    const target = modules[next];
    if (target == null) return;
    onChange(target);
    refs.current[next]?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        focusAndSelect(index + 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        focusAndSelect(index - 1);
        break;
      case "Home":
        e.preventDefault();
        focusAndSelect(0);
        break;
      case "End":
        e.preventDefault();
        focusAndSelect(modules.length - 1);
        break;
      default:
        break;
    }
  }

  // Ensure exactly one radio is tabbable: the selected one, or the first
  // when the current value isn't in this programme's module set.
  const selectedIndex = modules.indexOf(value);
  const tabbableIndex = selectedIndex >= 0 ? selectedIndex : 0;

  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
      {modules.map((m, i) => {
        const checked = value === m;
        return (
          <button
            key={m}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={i === tabbableIndex ? 0 : -1}
            data-state={checked ? "on" : "off"}
            onClick={() => onChange(m)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={cn(toggleVariants({ variant: "outline", size: "sm" }))}
          >
            {m === "all" ? "All" : MODULE_LABELS[m] ?? m}
          </button>
        );
      })}
    </div>
  );
}
